// frontend/src/utils/conversation/buildAdmissibilityState.js

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s'",.-]/g, '');
}

function countWords(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

function detectCorrection(message = '') {
  const lower = normalizeText(message);
  if (!lower) return false;

  return [
    'actually',
    'we are talking about',
    "we're talking about",
    'not her',
    'not him',
    'not them',
    'no that is',
    'oh no',
    'i meant',
    'rather',
    'instead',
  ].some((phrase) => lower.includes(phrase));
}

function detectEmotionalDisclosure(message = '') {
  const lower = normalizeText(message);
  if (!lower) return false;

  return [
    "i'm lonely",
    'im lonely',
    'i am lonely',
    'i miss',
    'missing',
    "i'm sad",
    'im sad',
    'i am sad',
    "i'm hurting",
    'im hurting',
    'i am hurting',
    "i'm overwhelmed",
    'im overwhelmed',
    'i am overwhelmed',
    "i'm struggling",
    'im struggling',
    'i am struggling',
    "i'm worried",
    'im worried',
    'i am worried',
    "i'm anxious",
    'im anxious',
    'i am anxious',
  ].some((phrase) => lower.includes(phrase));
}

function detectClosure(message = '') {
  const lower = normalizeText(message);
  if (!lower) return false;

  return [
    'never mind',
    'nevermind',
    'leave it',
    'leave it there',
    'forget it',
    'drop it',
    'its ok',
    "it's ok",
    'its okay',
    "it's okay",
    'thats okay',
    "that's okay",
    'thats fine',
    "that's fine",
  ].some((phrase) => lower === phrase || lower.includes(phrase));
}

function detectLowSignal(message = '') {
  const lower = normalizeText(message);
  const wordCount = countWords(lower);

  if (!lower) return true;
  if (wordCount <= 1) return true;

  return [
    'what',
    'ok',
    'okay',
    'k',
    'kk',
    'yes',
    'yeah',
    'yep',
    'no',
    'nope',
    'nah',
    'thanks',
    'thank you',
    'cool',
    'got it',
  ].includes(lower);
}

function computeAuthorityScores({
  message = '',
  pendingQuestionState = null,
  entityState = null,
  frame = null,
  explicitRelationshipDeclaration = null,
} = {}) {
  const lower = normalizeText(message);
  const wordCount = countWords(lower);

  const relationshipDeclaration = explicitRelationshipDeclaration ? 0.95 : 0;
  const correction = detectCorrection(lower) ? 0.92 : 0;
  const emotionalDisclosure = detectEmotionalDisclosure(lower) ? 0.82 : 0;
  const closure = detectClosure(lower) ? 0.7 : 0;
  const lowSignal = detectLowSignal(lower) ? 0.75 : 0;

  let pendingAnswer = 0;
  if (pendingQuestionState?.detectedAnswerToPendingQuestion === true) {
    const answerSignal = pendingQuestionState?.answerSignal || 'none';
    if (answerSignal === 'substantive') pendingAnswer = 0.7;
    else if (answerSignal === 'low') pendingAnswer = 0.4;
    else pendingAnswer = 0.2;
  }

  let entityContinuity = 0;
  if (entityState?.active) entityContinuity += 0.45;
  if (entityState?.previous) entityContinuity += 0.08;
  if (wordCount >= 4) entityContinuity += 0.05;
  entityContinuity = Math.max(0, Math.min(1, Number(entityContinuity.toFixed(2))));

  let frameContinuity = 0;
  if (frame?.type) frameContinuity += 0.35;
  if (frame?.confidence >= 0.7) frameContinuity += 0.15;
  frameContinuity = Math.max(0, Math.min(1, Number(frameContinuity.toFixed(2))));

  return {
    relationshipDeclaration,
    correction,
    emotionalDisclosure,
    pendingAnswer,
    closure,
    lowSignal,
    entityContinuity,
    frameContinuity,
  };
}

function getDominantAuthority(scores = {}) {
  const ordered = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [winner, winnerScore] = ordered[0] || ['none', 0];
  const [runnerUp, runnerUpScore] = ordered[1] || [null, 0];

  return {
    winner,
    winnerScore,
    runnerUp,
    runnerUpScore,
    margin: Math.max(0, Math.min(1, Number((winnerScore - runnerUpScore).toFixed(2)))),
  };
}

function buildAdmissibilityState({
  message = '',
  pendingQuestionState = null,
  entityState = null,
  frame = null,
  explicitRelationshipDeclaration = null,
} = {}) {
  const normalizedMessage = normalizeText(message);
  const scores = computeAuthorityScores({
    message: normalizedMessage,
    pendingQuestionState,
    entityState,
    frame,
    explicitRelationshipDeclaration,
  });

  const dominance = getDominantAuthority(scores);

  const identityOk = true;
  const frameOk = !!frame;
  const authorityOk = dominance.winnerScore > 0;

  const allowRelationshipDeclaration =
    scores.relationshipDeclaration >= 0.9;

  const allowCorrection =
    scores.correction >= 0.9;

  const allowEmotionalDisclosure =
    scores.emotionalDisclosure >= 0.75;

  const suppressPendingQuestionExecution =
    allowRelationshipDeclaration ||
    allowCorrection ||
    allowEmotionalDisclosure ||
    scores.lowSignal >= 0.7;

  const rejectAsGarbage =
    scores.lowSignal >= 0.7 &&
    !allowRelationshipDeclaration &&
    !allowCorrection &&
    !allowEmotionalDisclosure;

  return {
    identityOk,
    frameOk,
    authorityOk,
    dominantAuthority: dominance.winner,
    dominantAuthorityScore: dominance.winnerScore,
    runnerUpAuthority: dominance.runnerUp,
    runnerUpAuthorityScore: dominance.runnerUpScore,
    authorityMargin: dominance.margin,
    scores,
    control: {
      allowRelationshipDeclaration,
      allowCorrection,
      allowEmotionalDisclosure,
      suppressPendingQuestionExecution,
      rejectAsGarbage,
    },
    reasons: [
      allowRelationshipDeclaration ? 'relationship declaration admitted' : null,
      allowCorrection ? 'correction admitted' : null,
      allowEmotionalDisclosure ? 'emotional disclosure admitted' : null,
      suppressPendingQuestionExecution ? 'pending execution suppressed' : null,
      rejectAsGarbage ? 'low-signal rejected' : null,
    ].filter(Boolean),
  };
}

export { buildAdmissibilityState };